import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { IonContent, IonLoading, IonModal, IonPage, useIonViewWillEnter } from '@ionic/react'
import { MktEmpty, MktFooter, Stepper, UseHeader } from '@cps'
import { ActivityAPI, OrderAPI } from '@api/index'
import { memberMobx, storeMobx } from '@mobxStore'
import { getSourceType, toast, useHistoryNew } from '@utils'
import { toDecimal } from '@dt/fe-utils'
import CashierController from '@ctrls/cashierController'
import CheckoutPayController from '@ctrls/checkoutPayController'
import mktController from 'controllers/mktController'
import defaultImg from '@img/default-longMallProduct.png'
import { isActivityPayCancelError, isActivityPaymentEnvironment, payActivityReserveOrder } from '../payment'
import {
  formatDateOnlyRange,
  formatDateRange,
  formatPrice,
  getActivityName,
  getCover,
  getEndAt,
  getRemain,
  getSessionName,
  getStartAt,
  getStoreName,
  getTicketName,
  getTicketPrice,
  isFreeActivity,
  normalizeActivityDetail
} from '../utils'
import './index.scss'

interface IProps extends IPage, RouteComponentProps<{ activityId: string }> { }

const isSameId = (a: any, b: any) => a !== undefined && a !== null && a !== '' && b !== undefined && b !== null && b !== '' && String(a) === String(b)
const getStoreId = (item: any) => item?.storeId || item?.id
const getSessionId = (item: any) => item?.sessionId || item?.id
const getTicketId = (item: any) => item?.ticketId || item?.activityScheduleTicketId || item?.id
const getDateTimestamp = (value: any) => {
  if (!value) return 0
  const normalizedValue = typeof value === 'string' ? value.replace(' ', 'T') : value
  const timestamp = new Date(normalizedValue).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}
const isUnavailableSession = (item: any) => String(item?.status || '').toUpperCase() === 'UNAVAILABLE'
const isSessionDisabled = (item: any) => !item || isUnavailableSession(item) || getRemain(item) === 0
const isTicketDisabled = (item: any) => !item || getRemain(item) === 0 || item?.soldOut || item?.status === 'stopped'
const getStoreAddress = (item: any) => {
  const address = item?.address || item?.storeAddress || ''
  const addressDetails = item?.addressDetails || item?.addressDetail || ''
  if (addressDetails && address.includes(addressDetails)) return address
  return `${address}${addressDetails}`
}
const NAME_MAX_LENGTH = 12
type TSelectorType = 'store' | 'session' | 'ticket' | ''

const ActivityReserveBooking: FC<IProps> = ({ match }: IProps) => {
  const history = useHistoryNew()
  const { activityId } = match.params
  const [info, setInfo] = useState<any>({})
  const [selectedStoreId, setSelectedStoreId] = useState<string | number>('')
  const [selectedSessionId, setSelectedSessionId] = useState<string | number>('')
  const [selectedTicketId, setSelectedTicketId] = useState<string | number>('')
  const [form, setForm] = useState<{ name: string; mobile: string; quantity: number | string; remark: string }>({ name: '', mobile: '', quantity: 1, remark: '' })
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [selectorType, setSelectorType] = useState<TSelectorType>('')
  const submittingRef = useRef(false)

  const stores = useMemo(() => info?.stores || info?.storeList || [], [info])
  const sessions = useMemo(() => {
    const allSessions = info?.sessions || info?.sessionList || []
    const filteredSessions = !selectedStoreId ? allSessions : allSessions.filter((item: any) => {
      const storeIds = item?.storeIds?.length ? item.storeIds : (item?.stores?.map((store: any) => getStoreId(store)) || (item?.storeId ? [item.storeId] : []))
      return !storeIds?.length || storeIds.some((storeId: any) => isSameId(storeId, selectedStoreId))
    })
    return [...filteredSessions].sort((a: any, b: any) => getDateTimestamp(getStartAt(b)) - getDateTimestamp(getStartAt(a)))
  }, [info, selectedStoreId])
  const sessionOptions = useMemo(() => {
    return sessions
      .filter((item: any) => !isUnavailableSession(item))
      .sort((a: any, b: any) => getDateTimestamp(getStartAt(a)) - getDateTimestamp(getStartAt(b)))
  }, [sessions])
  const currentSession = useMemo(() => sessions.find((item: any) => isSameId(getSessionId(item), selectedSessionId)), [sessions, selectedSessionId])
  const tickets = useMemo(() => {
    const storeTickets = currentSession?.storeTicketsMap?.[String(selectedStoreId)]
    return storeTickets?.length ? storeTickets : (currentSession?.tickets || currentSession?.ticketList || [])
  }, [currentSession, selectedStoreId])
  const currentTicket = useMemo(() => tickets.find((item: any) => isSameId(getTicketId(item), selectedTicketId)), [tickets, selectedTicketId])
  const currentStore = useMemo(() => stores.find((item: any) => isSameId(getStoreId(item), selectedStoreId)), [stores, selectedStoreId])
  const isFree = isFreeActivity(info)
  const quantity = Math.max(1, Number(form.quantity || 1))
  const quantityRemain = getRemain(currentTicket || currentSession)
  const maxQuantity = Math.max(1, Math.min(99, quantityRemain > 0 ? quantityRemain : 99))
  const total = isFree ? 0 : getTicketPrice(currentTicket) * quantity
  const paymentAmountText = Number(total || 0) === 0 ? '¥0' : formatPrice(total)
  const hasAvailableSession = sessions.some((item: any) => !isSessionDisabled(item))
  const allSessionsExpired = Boolean(selectedStoreId && sessions.length && sessions.every((item: any) => isUnavailableSession(item)))
  const allTicketsSoldOut = Boolean(selectedSessionId && tickets.length && tickets.every((item: any) => isTicketDisabled(item)))
  const hasAvailableTicket = tickets.length ? Boolean(selectedTicketId && !isTicketDisabled(currentTicket)) : isFree
  const canSubmit = Boolean(selectedStoreId && selectedSessionId && !isSessionDisabled(currentSession) && hasAvailableTicket)
  const showReserveFooter = Boolean(Object.keys(info || {}).length && hasAvailableSession)
  const storeSelectorDisabled = !stores.length
  const sessionSelectorDisabled = !selectedStoreId || !sessions.length
  const ticketSelectorDisabled = !selectedStoreId || !selectedSessionId || !tickets.length
  const sessionPlaceholder = !selectedStoreId
    ? i18n.t('mkt.activityReserve.selectStoreFirst')
    : !sessions.length
      ? i18n.t('mkt.activityReserve.noSession')
      : allSessionsExpired
        ? i18n.t('mkt.activityReserve.allSessionsExpired')
        : i18n.t('mkt.activityReserve.pleaseSelectSession')

  const getData = () => {
    // 不传 storeId，避免接口按门店过滤 schedules；门店和门票都从 schedules 聚合生成。
    ActivityAPI.getActivityReserveDetail(activityId).then((res: any) => {
      if (!res?.errors) {
        // 新详情接口按 data 聚合返回，这里转换成页面既有的门店/场次/门票结构。
        const data = normalizeActivityDetail(res, storeMobx.viewStoreList || storeMobx.storeList || [storeMobx.info])
        setInfo(data)
        const dataStores = data.stores || data.storeList || []
        const onlyStore = dataStores.length === 1 ? dataStores[0] : undefined
        setSelectedStoreId(getStoreId(onlyStore) || '')
        setSelectedSessionId('')
        setSelectedTicketId('')
      }
    }).catch(() => setInfo({}))
  }

  useIonViewWillEnter(() => {
    getData()
  })

  useEffect(() => {
    const memberName = memberMobx.memberInfo?.nickName || memberMobx.memberInfo?.memberName || ''
    const memberMobile = memberMobx.memberInfo?.mobile || ''
    if (!memberName && !memberMobile) return

    setForm(prev => ({
      ...prev,
      name: prev.name || String(memberName).slice(0, NAME_MAX_LENGTH),
      mobile: prev.mobile || String(memberMobile),
    }))
  }, [memberMobx.memberInfo?.nickName, memberMobx.memberInfo?.memberName, memberMobx.memberInfo?.mobile])

  useEffect(() => {
    if (!selectedStoreId || !sessions.length) {
      if (selectedSessionId) setSelectedSessionId('')
      return
    }
    const selectedSession = sessions.find((item: any) => isSameId(getSessionId(item), selectedSessionId))
    if (!selectedSession || isSessionDisabled(selectedSession)) {
      // 自动选择只统计可用场次，接口标记为 UNAVAILABLE 的过期场次不参与数量判断。
      const onlySession = sessionOptions.length === 1 && !isSessionDisabled(sessionOptions[0]) ? sessionOptions[0] : undefined
      setSelectedSessionId(getSessionId(onlySession) || '')
    }
  }, [sessionOptions, sessions, selectedSessionId, selectedStoreId])

  useEffect(() => {
    if (!selectedSessionId || !tickets.length) {
      if (selectedTicketId) setSelectedTicketId('')
      return
    }
    const selectedTicket = tickets.find((item: any) => isSameId(getTicketId(item), selectedTicketId))
    if (!selectedTicket || isTicketDisabled(selectedTicket)) {
      const onlyTicket = tickets.length === 1 && !isTicketDisabled(tickets[0]) ? tickets[0] : undefined
      setSelectedTicketId(getTicketId(onlyTicket) || '')
    }
  }, [tickets, selectedSessionId, selectedTicketId])

  useEffect(() => {
    if (Number(form.quantity || 1) > maxQuantity) {
      setForm(prev => ({ ...prev, quantity: maxQuantity }))
    }
  }, [form.quantity, maxQuantity])

  const validate = () => {
    const name = form.name.trim()
    const mobile = form.mobile.trim()
    if (!canSubmit) {
      toast({ message: i18n.t('mkt.activityReserve.selectCompleteInfo') })
      return false
    }
    if (!name) {
      toast({ message: i18n.t('mkt.activityReserve.enterRegistrantName') })
      return false
    }
    if (name.length > NAME_MAX_LENGTH) {
      toast({ message: i18n.t('mkt.activityReserve.nameMaxLength', { count: NAME_MAX_LENGTH }) })
      return false
    }
    if (!mobile) {
      toast({ message: i18n.t('mkt.activityReserve.enterMobile') })
      return false
    }
    if (!/^1\d{10}$/.test(mobile)) {
      toast({ message: i18n.t('mkt.activityReserve.invalidMobile') })
      return false
    }
    if (getRemain(currentSession) === 0) {
      toast({ message: i18n.t('mkt.activityReserve.sessionFull') })
      return false
    }
    if (isUnavailableSession(currentSession)) {
      toast({ message: i18n.t('mkt.activityReserve.sessionExpired') })
      return false
    }
    if (!isFree && (getRemain(currentTicket) === 0 || currentTicket?.soldOut)) {
      toast({ message: i18n.t('mkt.activityReserve.ticketSoldOut') })
      return false
    }
    if (isFree && tickets.length && !hasAvailableTicket) {
      toast({ message: i18n.t('mkt.activityReserve.freeTicketFull') })
      return false
    }
    if (quantity > maxQuantity) {
      toast({ message: i18n.t('mkt.activityReserve.maxBookable', { count: maxQuantity }) })
      return false
    }
    return true
  }

  const submit = () => {
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)

    mktController.checkLogin(history, true, false).then(() => {
      if (!validate()) return undefined
      if (!isFree && !isActivityPaymentEnvironment()) {
        toast({ message: i18n.t('mkt.activityReserve.completePaymentInApp') })
        return undefined
      }
      const name = form.name.trim()
      const mobile = form.mobile.trim()
      const lineAmount = toDecimal(total)
      const memberId = memberMobx.memberInfo?.memberId ? String(memberMobx.memberInfo.memberId) : '0'
      const payload: any = {
        infos: {
          startTime: undefined,
          deviceId: 'mobile',
          sourceType: getSourceType(Config, (storeMobx.info?.storeOrderType || 0) as 0 | 1),
          brandPromotionId: Config.promotionId || undefined,
          brandId: Config.BRANDID,
          storeId: selectedStoreId,
          memberId,
          memberGroupId: '0',
          channel: 'mobile',
          appType: 'mobile',
          eatType: 'activity',
          ordersType: 'goods',
          bo,
          currency: bo === 'cn' ? 'RMB' : bo === 'de' ? 'EUR' : 'USD',
          payMode: 0,
          originalFee: lineAmount,
          paidFee: lineAmount,
          mktFee: 0,
          tax: 0,
          freeTax: 0,
          serviceCharge: 0,
          box: 0,
          sk: false,
          subject: i18n.t('mkt.activityReserve.paymentSubject', { name: getActivityName(info) }),
          remark: form.remark,
          contactInfo: JSON.stringify({
            memberMobile: memberMobx.memberInfo?.mobile,
            memberEmail: memberMobx.memberInfo?.email
          }),
          contactMobile: mobile,
          contactName: name,
          openId: memberMobx.memberInfo?.openId,
          menuType: 'mobile',
        },
        ordersDetails: [
          {
            type: 'activity',
            productNo: activityId,
            productScaleNo: selectedTicketId,
            cnt: quantity,
            paidFee: lineAmount,
            originalFee: lineAmount,
          }
        ]
      }
      // 活动预约保存订单后交给独立支付模块处理，不进入通用支付页。
      let createdOrdersId = ''
      return OrderAPI.save(payload).then(async (res: any) => {
        if (res?.result) {
          const ordersId = res.result.ordersId || res.result.orderId
          createdOrdersId = ordersId
          if (ordersId) {
            const payResult = await payActivityReserveOrder({
              ordersId,
              amount: Number(lineAmount),
              storeId: Number(selectedStoreId) || 0,
              activityId,
            })
            if (payResult === 'success') {
              CashierController.toResult(ordersId)
            }
          } else {
            history.replace('/activityReserve/records')
          }
        }
      }).catch(async (err: any) => {
        if (isActivityPayCancelError(err)) {
          if (createdOrdersId) {
            try {
              await CheckoutPayController.resume(createdOrdersId)
              await OrderAPI.cancel({
                ordersId: createdOrdersId,
                storeId: Number(selectedStoreId) || 0,
                brandId: Config.BRANDID,
                memberId: memberMobx.memberInfo?.memberId,
                cancelReason: 'user cancel payment',
              })
            } catch (error) {}
          }
          return
        }
        if (err?.message) {
          toast({ message: err.message })
        }
      })
    }).finally(() => {
      submittingRef.current = false
      setSubmitting(false)
    })
  }

  const updateQuantity = (value: number) => {
    setForm(prev => ({ ...prev, quantity: Math.max(1, Math.min(maxQuantity, Number(value || 1))) }))
  }

  const handleQuantityStep = (isAdd: boolean, event: React.MouseEvent) => {
    event.stopPropagation()
    updateQuantity(quantity + (isAdd ? 1 : -1))
  }

  const handleQuantityEdit = (cnt: number, event: React.MouseEvent) => {
    event.stopPropagation()
    updateQuantity(cnt)
  }

  const coverUrl = getCover(info) || defaultImg

  const previewImage = (url?: string) => {
    if (url) setPreviewUrl(url)
  }

  const openSelector = (type: Exclude<TSelectorType, ''>) => {
    if (type === 'store' && !stores.length) {
      toast({ message: i18n.t('mkt.activityReserve.noAvailableStore') })
      return
    }
    if (type === 'session' && !selectedStoreId) {
      toast({ message: i18n.t('mkt.activityReserve.selectStoreFirst') })
      return
    }
    if (type === 'session' && !sessionOptions.length) {
      toast({ message: allSessionsExpired ? i18n.t('mkt.activityReserve.allSessionsExpired') : i18n.t('mkt.activityReserve.noAvailableSession') })
      return
    }
    if (type === 'ticket' && !selectedStoreId) {
      toast({ message: i18n.t('mkt.activityReserve.selectStoreFirst') })
      return
    }
    if (type === 'ticket' && !selectedSessionId) {
      toast({ message: i18n.t('mkt.activityReserve.selectSessionFirst') })
      return
    }
    if (type === 'ticket' && !tickets.length) {
      toast({ message: isFree ? i18n.t('mkt.activityReserve.activityNoTicket') : i18n.t('mkt.activityReserve.noAvailableTicket') })
      return
    }
    setSelectorType(type)
  }

  const handleSelectStore = (item: any) => {
    const storeId = getStoreId(item)
    if (!isSameId(selectedStoreId, storeId)) {
      setSelectedSessionId('')
      setSelectedTicketId('')
    }
    setSelectedStoreId(storeId)
    setSelectorType('')
  }

  const handleSelectSession = (item: any) => {
    if (isSessionDisabled(item)) return
    const sessionId = getSessionId(item)
    if (!isSameId(selectedSessionId, sessionId)) setSelectedTicketId('')
    setSelectedSessionId(sessionId)
    setSelectorType('')
  }

  const handleSelectTicket = (item: any) => {
    if (isTicketDisabled(item)) return
    setSelectedTicketId(getTicketId(item))
    setSelectorType('')
  }

  return (
    <IonPage className='activity-reserve-booking-page'>
      <UseHeader title={i18n.t('mkt.activityReserve.bookingTitle')} onBack={() => mktController.goBack(history)}></UseHeader>
      <IonContent className='page-content'>
        {!info || Object.keys(info).length === 0 ? <MktEmpty /> : (
          <div className='activity-booking-body'>
            <div className='activity-booking-summary'>
              <div className='activity-summary-cover' style={{ backgroundImage: `url(${coverUrl})` }} onClick={() => previewImage(coverUrl)}></div>
              <div className='activity-summary-content'>
                <h2>{getActivityName(info)}</h2>
                <span>{formatDateOnlyRange(getStartAt(info), getEndAt(info))}</span>
              </div>
            </div>

            <section className='activity-section'>
              <h3>{i18n.t('mkt.activityReserve.selectStore')}</h3>
              <button className={`selector-trigger${currentStore ? '' : ' unselected'}${storeSelectorDisabled ? ' disabled' : ''}`} aria-disabled={storeSelectorDisabled} onClick={() => openSelector('store')}>
                <span className='selector-value'>
                  <strong className={currentStore ? '' : 'placeholder'}>{currentStore ? getStoreName(currentStore) : i18n.t('mkt.activityReserve.pleaseSelectStore')}</strong>
                  {currentStore && getStoreAddress(currentStore) && <span>{getStoreAddress(currentStore)}</span>}
                </span>
                <i className='iconfont icon-chevron-right'></i>
              </button>
            </section>

            <section className='activity-section'>
              <h3>{i18n.t('mkt.activityReserve.selectSession')}</h3>
              <button className={`selector-trigger${currentSession ? '' : ' unselected'}${sessionSelectorDisabled ? ' disabled' : ''}`} aria-disabled={sessionSelectorDisabled} onClick={() => openSelector('session')}>
                <span className='selector-value'>
                  <strong className={currentSession ? '' : 'placeholder'}>{currentSession ? getSessionName(currentSession) : sessionPlaceholder}</strong>
                  {currentSession && <span>{formatDateRange(getStartAt(currentSession), getEndAt(currentSession))}</span>}
                </span>
                <i className='iconfont icon-chevron-right'></i>
              </button>
            </section>

            <section className='activity-section'>
              <h3>{i18n.t('mkt.activityReserve.selectTicket')}</h3>
              <button className={`selector-trigger${currentTicket ? '' : ' unselected'}${ticketSelectorDisabled ? ' disabled' : ''}`} aria-disabled={ticketSelectorDisabled} onClick={() => openSelector('ticket')}>
                <span className='selector-value selector-ticket-value'>
                  <strong className={currentTicket ? '' : 'placeholder'}>
                    {currentTicket
                      ? getTicketName(currentTicket)
                      : !selectedSessionId
                        ? i18n.t('mkt.activityReserve.selectSessionFirst')
                        : allTicketsSoldOut
                          ? i18n.t('mkt.activityReserve.noStock')
                          : tickets.length
                            ? i18n.t('mkt.activityReserve.pleaseSelectTicket')
                            : isFree
                              ? i18n.t('mkt.activityReserve.noTicketRequired')
                              : i18n.t('mkt.activityReserve.noTicket')}
                  </strong>
                  {currentTicket && (
                    <span>
                      <em>{Number(getTicketPrice(currentTicket) || 0) === 0 ? '¥0' : formatPrice(getTicketPrice(currentTicket))}</em>
                      {isTicketDisabled(currentTicket) && <small className='ticket-unavailable'>{i18n.t('mkt.activityReserve.noStock')}</small>}
                    </span>
                  )}
                </span>
                <i className='iconfont icon-chevron-right'></i>
              </button>
            </section>

            {hasAvailableSession && (
              <section className='activity-section'>
                <h3>{i18n.t('mkt.activityReserve.registrationInfo')}</h3>
                <div className='booking-form'>
                  <label><span className='required'>{i18n.t('mkt.activityReserve.name')}</span><input value={form.name} required maxLength={NAME_MAX_LENGTH} placeholder={i18n.t('mkt.activityReserve.enterName')} onChange={(e: any) => setForm({ ...form, name: e.target.value.slice(0, NAME_MAX_LENGTH) })} /></label>
                  <label><span className='required'>{i18n.t('mkt.activityReserve.mobile')}</span><input value={form.mobile} required type='tel' maxLength={11} placeholder={i18n.t('mkt.activityReserve.enterMobile')} onChange={(e: any) => setForm({ ...form, mobile: e.target.value })} /></label>
                  <label className='textarea-row'><span>{i18n.t('mkt.activityReserve.remark')}</span><textarea value={form.remark} maxLength={200} placeholder={i18n.t('mkt.activityReserve.optional')} onChange={(e: any) => setForm({ ...form, remark: e.target.value })} /></label>
                </div>
              </section>
            )}

          </div>
        )}
      </IonContent>
      {previewUrl && (
        <div className='image-preview-mask' onClick={() => setPreviewUrl('')}>
          <img src={previewUrl} alt='' onClick={(e: React.MouseEvent<HTMLImageElement>) => e.stopPropagation()} />
        </div>
      )}
      <IonLoading isOpen={submitting} backdropDismiss={false} />
      <IonModal
        isOpen={Boolean(selectorType)}
        animated={false}
        cssClass='activity-reserve-select-modal'
        showBackdrop
        swipeToClose
        backdropDismiss
        onDidDismiss={() => setSelectorType('')}
      >
        <div className='select-modal-header'>
          <h3>{selectorType === 'store' ? i18n.t('mkt.activityReserve.selectStore') : selectorType === 'session' ? i18n.t('mkt.activityReserve.selectSession') : selectorType === 'ticket' ? i18n.t('mkt.activityReserve.selectTicket') : ''}</h3>
          <button aria-label={i18n.t('mkt.activityReserve.close')} onClick={() => setSelectorType('')}><i className='iconfont icon-close'></i></button>
        </div>
        <IonContent className='select-modal-content'>
          <div className='select-modal-list'>
            {selectorType === 'store' && stores.map((item: any) => {
              const id = getStoreId(item)
              return (
                <button key={id} className={isSameId(selectedStoreId, id) ? 'selected' : ''} onClick={() => handleSelectStore(item)}>
                  <strong>{getStoreName(item)}</strong>
                  {getStoreAddress(item) && <span>{getStoreAddress(item)}</span>}
                </button>
              )
            })}
            {selectorType === 'session' && sessionOptions.map((item: any) => {
              const id = getSessionId(item)
              const remain = getRemain(item)
              const disabled = isSessionDisabled(item)
              return (
                <button key={id} disabled={disabled} className={`${isSameId(selectedSessionId, id) ? 'selected' : ''}${disabled ? ' disabled' : ''}`} onClick={() => handleSelectSession(item)}>
                  <strong>{getSessionName(item)}</strong>
                  <span className='select-option-meta'>
                    <span>{formatDateRange(getStartAt(item), getEndAt(item))}</span>
                    {disabled && <em>{i18n.t('mkt.activityReserve.fullyBooked')}</em>}
                    {!disabled && remain >= 0 && <em>{i18n.t('mkt.activityReserve.remaining', { count: remain })}</em>}
                  </span>
                </button>
              )
            })}
            {selectorType === 'ticket' && tickets.map((item: any) => {
              const id = getTicketId(item)
              const disabled = isTicketDisabled(item)
              return (
                <button key={id} disabled={disabled} className={`${isSameId(selectedTicketId, id) ? 'selected' : ''}${disabled ? ' disabled' : ''}`} onClick={() => handleSelectTicket(item)}>
                  <strong>{getTicketName(item)}</strong>
                  <span className='select-option-meta ticket-option-meta'>
                    <span>{Number(getTicketPrice(item) || 0) === 0 ? '¥0' : formatPrice(getTicketPrice(item))}</span>
                    {disabled && <em className='ticket-unavailable'>{i18n.t('mkt.activityReserve.noStock')}</em>}
                  </span>
                </button>
              )
            })}
          </div>
        </IonContent>
      </IonModal>
      {showReserveFooter && (
        <MktFooter className='activity-reserve-footer'>
          <div className='footer-stepper'>
            {hasAvailableTicket && <Stepper min={1} max={maxQuantity} cnt={quantity} onChange={handleQuantityStep} onEdit={handleQuantityEdit} />}
          </div>
          <button disabled={!canSubmit || submitting} onClick={submit}>
            {isFree
              ? i18n.t('mkt.activityReserve.confirm')
              : currentTicket
                ? i18n.t('mkt.activityReserve.payNowAmount', { amount: paymentAmountText })
                : i18n.t('mkt.activityReserve.payNow')}
          </button>
        </MktFooter>
      )}
    </IonPage>
  )
}

export default observer(ActivityReserveBooking)
